import React, { useState, useEffect } from 'react';
import { Calendar, Clock, User, Mail, Video, MapPin, CheckCircle2, AlertCircle, Loader, Check, X, RefreshCw } from 'lucide-react';
import { sanitizeNamePart } from '../lib/nameUtils';

interface GroupEventPublicData {
  eventId: string;
  title: string;
  eventType: string;
  dateTime: string;
  timezone: string;
  location: string;
  description: string;
  status: string;
  groupEventConfig: {
    sessionDuration: number;
    isVirtual: boolean;
    location: string;
    bookingOpensType: 'immediately' | 'scheduled';
    bookingOpensAt?: string;
    bookingClosesType: 'hoursBefore' | 'scheduled';
    bookingClosesHours?: number;
    bookingClosesAt?: string;
  };
  slots: {
    id: string;
    startTime: string;
    endTime: string;
    isBooked: boolean;
    bookedBy?: {
      name: string;
      email: string;
      applicationId: string;
    };
    bookingStatus?: string;
    meetingUrl?: string;
  }[];
}

// Robust guess-and-adjust timezone conversion algorithm
function getLocalTimeForSlot(baseDateStr: string, slotTimeStr: string, eventTimezone: string): { localTimeStr: string, localDateStr: string } {
  try {
    const datePart = baseDateStr.split('T')[0]; // "YYYY-MM-DD"

    // Get formatted ISO-like string in the target timezone
    const getOffsetString = (tz: string, date: Date) => {
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: tz,
        year: 'numeric', month: 'numeric', day: 'numeric',
        hour: 'numeric', minute: 'numeric', second: 'numeric',
        hour12: false
      });
      const parts = formatter.formatToParts(date);
      const getVal = (type: string) => parts.find(p => p.type === type)?.value || '';
      const year = getVal('year');
      const month = String(getVal('month')).padStart(2, '0');
      const day = String(getVal('day')).padStart(2, '0');
      const hour = String(getVal('hour')).padStart(2, '0');
      const minute = String(getVal('minute')).padStart(2, '0');
      const second = String(getVal('second')).padStart(2, '0');
      return `${year}-${month}-${day}T${hour}:${minute}:${second}.000Z`;
    };

    // Guess UTC epoch milliseconds
    let guess = new Date(`${datePart}T${slotTimeStr}:00Z`);
    for (let i = 0; i < 3; i++) {
      const formatted = getOffsetString(eventTimezone, guess);
      const diff = new Date(`${datePart}T${slotTimeStr}:00.000Z`).getTime() - new Date(formatted).getTime();
      guess = new Date(guess.getTime() + diff);
    }

    const localTimeStr = guess.toLocaleTimeString(undefined, {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });

    const localDateStr = guess.toLocaleDateString(undefined, {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });

    return { localTimeStr, localDateStr };
  } catch (err) {
    // Fallback if timezone conversion fails
    const [h, m] = slotTimeStr.split(':');
    const hr = parseInt(h, 10);
    const ampm = hr >= 12 ? 'PM' : 'AM';
    const hour12 = hr % 12 || 12;
    return {
      localTimeStr: `${hour12}:${m} ${ampm}`,
      localDateStr: new Date(baseDateStr).toLocaleDateString(undefined, {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric'
      })
    };
  }
}

export default function PublicGroupScheduleView({ eventId }: { eventId: string }) {
  const [eventData, setEventData] = useState<GroupEventPublicData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // ADM-01 close-out (Batch R2): booking-form validation and submission
  // failures render in an in-page banner (the PublicSigningView APP-04
  // pattern), never a native alert.
  const [formError, setFormError] = useState<string | null>(null);

  // Form states
  const [selectedSlotId, setSelectedSlotId] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [applicationId, setApplicationId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [bookedSlotInfo, setBookedSlotInfo] = useState<any>(null);

  // Magic Link states
  const [token, setToken] = useState<string | null>(null);
  const [verifyingToken, setVerifyingToken] = useState(false);
  const [tokenVerificationSuccess, setTokenVerificationSuccess] = useState<string | null>(null);

  // Rescheduling states
  const [isRescheduling, setIsRescheduling] = useState(false);
  const [showConfirmReschedule, setShowConfirmReschedule] = useState(false);
  const [originalBookedSlot, setOriginalBookedSlot] = useState<any>(null);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const tokenParam = searchParams.get('token');
    if (tokenParam) {
      setToken(tokenParam);
      validateToken(tokenParam);
    }
    fetchEventDetails();
  }, [eventId]);

  // Reactive state: detect if current user is already scheduled
  useEffect(() => {
    if (applicationId && eventData && eventData.slots) {
      const alreadyBookedSlot = eventData.slots.find((s: any) => s.bookedBy?.applicationId === applicationId);
      if (alreadyBookedSlot) {
        setBookedSlotInfo(alreadyBookedSlot);
        setOriginalBookedSlot(alreadyBookedSlot);
        setSuccess(true);
      }
    }
  }, [applicationId, eventData]);

  const validateToken = async (tk: string) => {
    try {
      setVerifyingToken(true);
      const res = await fetch(`/api/validate-group-invite?token=${encodeURIComponent(tk)}`);
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || 'Your invitation link is invalid or has expired.');
      }
      const data = await res.json();
      const verifiedName = sanitizeNamePart(data.name) || sanitizeNamePart(data.userName);
      setName(verifiedName);
      setEmail(data.userEmail || '');
      setApplicationId(data.applicationId || '');
      setTokenVerificationSuccess(`Invitation verified for ${verifiedName || 'you'}.`);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setVerifyingToken(false);
    }
  };

  const fetchEventDetails = async () => {
    try {
      setLoading(true);
      const res = await fetch(`/api/public/group-events/${eventId}`);
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || 'Failed to fetch event details.');
      }
      const data = await res.json();
      setEventData(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleBookSlot = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    if (!selectedSlotId) {
      setFormError('Please select a time slot first.');
      return;
    }

    if (!token) {
      if (!name.trim()) {
        setFormError('Please enter your full name.');
        return;
      }
      if (!email.trim()) {
        setFormError('Please enter your email address.');
        return;
      }
      if (!applicationId.trim()) {
        setFormError('Please enter your Application ID.');
        return;
      }
    }

    try {
      setSubmitting(true);
      // Revalidate slot availability before reserving
      const checkRes = await fetch(`/api/public/group-events/${eventId}`);
      if (checkRes.ok) {
        const latestData = await checkRes.json();
        const targetSlot = latestData.slots?.find((s: any) => s.id === selectedSlotId);
        if (targetSlot && targetSlot.isBooked) {
          throw new Error('This slot was just booked by another applicant. Please select a different time slot.');
        }
      }

      const res = await fetch(`/api/public/group-events/${eventId}/book-slot`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          slotId: selectedSlotId,
          name: name.trim(),
          email: email.trim(),
          applicationId: applicationId.trim() || undefined,
          token: token || undefined,
          rescheduleOldSlotId: isRescheduling && originalBookedSlot ? originalBookedSlot.id : undefined
        })
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || 'Failed to book slot.');
      }

      const result = await res.json();
      setBookedSlotInfo(result.slot);
      setOriginalBookedSlot(result.slot);
      setSuccess(true);
      setIsRescheduling(false);
      setShowConfirmReschedule(false);
    } catch (err: any) {
      setFormError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const renderMarkdown = (md: string) => {
    if (!md) return null;
    const paragraphs = md.split('\n\n');
    return paragraphs.map((p, idx) => {
      if (p.startsWith('- ') || p.startsWith('* ')) {
        const items = p.split('\n').map(item => item.replace(/^[-*]\s+/, ''));
        return (
          <ul key={idx} className="list-disc pl-5 my-2 space-y-1 text-slate-600 text-xs">
            {items.map((item, i) => <li key={i}>{item}</li>)}
          </ul>
        );
      }
      if (p.startsWith('#')) {
        const level = (p.match(/^#+/) || ['#'])[0].length;
        const text = p.replace(/^#+\s+/, '');
        const classes = level === 1 ? 'text-lg font-bold text-slate-900 mt-4 mb-2'
                      : level === 2 ? 'text-base font-bold text-slate-900 mt-3 mb-2'
                      : 'text-sm font-bold text-slate-900 mt-2 mb-1';
        return React.createElement(`h${Math.min(level, 6)}`, { key: idx, className: classes }, text);
      }
      if (p.startsWith('> ')) {
        return (
          <blockquote key={idx} className="border-l-4 border-slate-300 pl-4 italic my-2 text-slate-500 text-xs">
            {p.slice(2)}
          </blockquote>
        );
      }
      return <p key={idx} className="my-2 leading-relaxed text-slate-600 text-xs">{p}</p>;
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-4">
        <div className="text-center space-y-3">
          <Loader className="h-8 w-8 animate-spin text-blue-600 mx-auto" />
          <p className="text-xs text-slate-500 font-semibold">Loading scheduling options...</p>
        </div>
      </div>
    );
  }

  if (verifyingToken) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-4">
        <div className="text-center space-y-3">
          <Loader className="h-8 w-8 animate-spin text-blue-600 mx-auto" />
          <p className="text-xs text-slate-500 font-semibold">Verifying your invitation...</p>
        </div>
      </div>
    );
  }

  if (error || !eventData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center space-y-4">
          <div className="mx-auto w-12 h-12 rounded-full bg-red-50 flex items-center justify-center text-red-500">
            <AlertCircle className="h-6 w-6" />
          </div>
          <h2 className="text-base font-extrabold text-slate-900">Link Unavailable</h2>
          <p className="text-xs text-slate-500 leading-normal">{error || 'This group event is no longer accepting bookings.'}</p>
        </div>
      </div>
    );
  }

  const { title, description, dateTime, timezone, slots, groupEventConfig } = eventData;
  const isEventCanceled = eventData.status === 'Cancelled' || eventData.status === 'canceled' || eventData.status === 'Cancelled by Admin';

  // Available slots are those that are not booked
  const availableSlots = slots.filter(s => !s.isBooked);

  // Check booking timeline boundaries
  const isBookingWindowClosed = () => {
    const now = new Date();

    // Check booking opens
    if (groupEventConfig.bookingOpensType === 'scheduled' && groupEventConfig.bookingOpensAt) {
      if (now < new Date(groupEventConfig.bookingOpensAt)) {
        return { closed: true, reason: `Booking has not opened yet. It will open on ${new Date(groupEventConfig.bookingOpensAt).toLocaleString()}` };
      }
    }

    // Check booking closes
    if (groupEventConfig.bookingClosesType === 'scheduled' && groupEventConfig.bookingClosesAt) {
      if (now > new Date(groupEventConfig.bookingClosesAt)) {
        return { closed: true, reason: 'The booking deadline for this event has passed.' };
      }
    } else if (groupEventConfig.bookingClosesType === 'hoursBefore' && groupEventConfig.bookingClosesHours) {
      const eventStart = new Date(dateTime);
      const deadline = new Date(eventStart.getTime() - groupEventConfig.bookingClosesHours * 60 * 60 * 1000);
      if (now > deadline) {
        return { closed: true, reason: `Booking closed ${groupEventConfig.bookingClosesHours} hours before the event.` };
      }
    }

    return { closed: false };
  };

  const bookingStatus = isBookingWindowClosed();
  const isEventClosed = eventData.status === 'closed' || bookingStatus.closed;

  // Canceled event display
  if (isEventCanceled) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-lg w-full text-center space-y-6 animate-fade-in">
          <div className="w-16 h-16 rounded-full bg-red-50 text-red-500 flex items-center justify-center mx-auto">
            <AlertCircle className="h-10 w-10" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-slate-900">Event Cancelled</h2>
            <p className="text-xs text-slate-500 mt-1">This group scheduling event has been cancelled by the workspace coordinator.</p>
          </div>
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-left space-y-2 text-xs text-slate-700">
            <p><strong>Event:</strong> {title}</p>
            <p><strong>Original Date:</strong> {new Date(dateTime).toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}</p>
          </div>
          <div className="text-xs text-slate-400 font-medium">
            Please contact your administrator or check your email inbox for updates.
          </div>
        </div>
      </div>
    );
  }

  // Helper functions for partial hashing
  const hashEmail = (rawEmail: string) => {
    if (!rawEmail || !rawEmail.includes('@')) return rawEmail;
    const [user, domain] = rawEmail.split('@');
    if (user.length <= 2) return `${user[0]}***@${domain}`;
    return `${user.substring(0, 2)}***${user.substring(user.length - 1)}@${domain}`;
  };

  const hashAppId = (id: string) => {
    if (!id || id.length <= 4) return id ? '***' : '';
    return `${id.substring(0, 4)}****${id.substring(id.length - 2)}`;
  };

  const hashName = (rawName: string) => {
    const clean = sanitizeNamePart(rawName);
    if (!clean) return '';
    const parts = clean.split(' ').filter(Boolean);
    if (parts.length === 1) {
      return parts[0].length <= 2 ? `${parts[0]}***` : `${parts[0].substring(0, 2)}***`;
    }
    const first = parts[0];
    const last = parts[parts.length - 1];
    const maskedFirst = first.length <= 2 ? `${first[0]}*` : `${first.substring(0, 2)}***`;
    const maskedLast = last.length <= 2 ? `${last[0]}*` : `${last.substring(0, 1)}***`;
    return `${maskedFirst} ${maskedLast}`;
  };

  // Already Booked or Pending Approval state (when not currently in rescheduling layout)
  if (success && !isRescheduling) {
    const { localTimeStr, localDateStr } = getLocalTimeForSlot(dateTime, bookedSlotInfo?.startTime || '', timezone);
    const safeCandidateName = sanitizeNamePart(name) || 'Applicant';
    const isPendingApproval = bookedSlotInfo?.bookingStatus === 'pending' || bookedSlotInfo?.checkInStatus === 'pending_approval';

    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-lg w-full text-center space-y-6 animate-fade-in">
          {isPendingApproval ? (
            <div className="w-16 h-16 rounded-full bg-amber-50 text-amber-500 flex items-center justify-center mx-auto">
              <Clock className="h-10 w-10" />
            </div>
          ) : (
            <div className="w-16 h-16 rounded-full bg-emerald-50 text-emerald-500 flex items-center justify-center mx-auto">
              <CheckCircle2 className="h-10 w-10" />
            </div>
          )}

          <div>
            <h2 className="text-lg font-bold text-slate-900">
              {isPendingApproval ? 'Booking Pending Approval' : (originalBookedSlot ? 'Event Rescheduled' : 'Event Booked')}
            </h2>
            <p className="text-xs text-slate-500 mt-1">
              {isPendingApproval
                ? 'Your booking request has been submitted and is awaiting administrator approval.'
                : 'You are currently scheduled for this event.'}
            </p>
          </div>

          <div className={`border rounded-xl p-4 text-left space-y-3 ${isPendingApproval ? 'bg-amber-50/50 border-amber-200' : 'bg-slate-50 border-slate-200'}`}>
            <div className="flex items-center justify-between">
              <h4 className="text-[10px] uppercase font-bold tracking-wider text-slate-400">
                {isPendingApproval ? 'Requested Appointment Details' : 'Your Appointment Details'}
              </h4>
              {isPendingApproval && (
                <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-800 border border-amber-200">
                  Pending Admin Approval
                </span>
              )}
            </div>

            <div className="space-y-2 text-xs text-slate-700">
              <p><strong>Candidate Name:</strong> {safeCandidateName}</p>
              <p><strong>Event:</strong> {title}</p>
              <p><strong>Date:</strong> {localDateStr}</p>
              <p><strong>Time Slot:</strong> {localTimeStr} (shown in your local timezone)</p>
              <p><strong>Host Timezone:</strong> {timezone}</p>
              <p><strong>Location:</strong> {groupEventConfig.isVirtual ? 'Virtual Meeting Room' : eventData.location}</p>
              {!isPendingApproval && bookedSlotInfo?.meetingUrl && (
                <div className="mt-3 pt-2 border-t border-slate-200">
                  <a
                    href={bookedSlotInfo.meetingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 px-4 py-2 bg-success hover:bg-[color-mix(in_srgb,var(--color-success)_90%,#000)] text-white rounded-xl text-[11px] font-bold transition-all"
                  >
                    <Video className="h-3.5 w-3.5" />
                    Join Meeting Room
                  </a>
                </div>
              )}
            </div>
          </div>

          {!isPendingApproval ? (
            <div className="pt-2 border-t border-slate-100 flex flex-col gap-2">
              <button
                onClick={() => {
                  setIsRescheduling(true);
                  setSelectedSlotId(null);
                }}
                className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl text-xs transition-all shadow-md shadow-blue-500/10 cursor-pointer flex items-center justify-center gap-1.5"
              >
                <RefreshCw className="h-4 w-4" />
                <span>Reschedule Event</span>
              </button>
              <p className="text-[10px] text-slate-400">Your current booking will remain safe and active unless you select and confirm a new slot.</p>
            </div>
          ) : (
            <div className="pt-2 border-t border-slate-100 text-center">
              <p className="text-[11px] text-slate-500">
                An administrator will review your request shortly. You will receive an email notification as soon as your booking is confirmed or updated.
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  const selectedSlot = slots.find(s => s.id === selectedSlotId);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-10 px-4">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Banner Card */}
        <div className="bg-white rounded-2xl shadow-lg border border-slate-100 overflow-hidden">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-6 text-white space-y-2">
            <div className="flex justify-between items-center">
              <span className="bg-white/20 text-white text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-lg backdrop-blur-sm">
                {eventData.eventType}
              </span>
              {isRescheduling && (
                <span className="bg-amber-500 text-white text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-lg">
                  Rescheduling Mode
                </span>
              )}
            </div>
            <h1 className="text-xl font-extrabold font-display leading-tight">{title}</h1>
            <p className="text-xs text-blue-100 flex items-center gap-1.5">
              <Calendar className="h-3.5 w-3.5 text-blue-200" />
              {new Date(dateTime).toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
            </p>
          </div>

          <div className="p-6 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs text-slate-600">
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-slate-400 shrink-0" />
                <span><strong>Location:</strong> {groupEventConfig.isVirtual ? 'Virtual Jitsi Meeting' : eventData.location}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-slate-400 shrink-0" />
                <span><strong>Duration:</strong> {groupEventConfig.sessionDuration} minutes</span>
              </div>
            </div>

            {description && (
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-1">
                <h4 className="text-[10px] uppercase font-bold tracking-wider text-slate-400">Instructions & Notes</h4>
                <div>{renderMarkdown(description)}</div>
              </div>
            )}
          </div>
        </div>

        {/* Rescheduling Active Notice Banner */}
        {isRescheduling && originalBookedSlot && (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-center justify-between text-xs text-amber-800">
            <div>
              <p className="font-bold">Rescheduling your appointment</p>
              <p className="text-[11px] mt-0.5 text-amber-700">Your current slot is <strong>{originalBookedSlot.startTime} - {originalBookedSlot.endTime}</strong>. It will be protected until you confirm a new time.</p>
            </div>
            <button
              onClick={() => {
                setIsRescheduling(false);
                setSelectedSlotId(null);
                setShowConfirmReschedule(false);
              }}
              className="px-3 py-1.5 bg-white border border-amber-300 hover:bg-amber-100 text-amber-800 font-bold rounded-xl cursor-pointer transition-all"
            >
              Cancel Rescheduling
            </button>
          </div>
        )}

        {/* Slot Selection Block */}
        <div className="bg-white rounded-2xl shadow-lg border border-slate-100 p-6 space-y-6">
          <div className="flex justify-between items-start flex-wrap gap-2">
            <div>
              <h3 className="text-sm font-bold text-slate-900">Available Appointment Slots</h3>
              <p className="text-xs text-slate-500 mt-0.5">Select your preferred slot from the remaining {availableSlots.length} openings.</p>
            </div>
            <span className="text-[11px] bg-blue-50 text-blue-600 px-2.5 py-1 rounded-full font-bold">
              Times shown in your local timezone.
            </span>
          </div>

          {availableSlots.length === 0 ? (
            <div className="text-center py-10 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-400 font-medium">
              All slots are currently booked. Please contact your coordinator to inquire about waitlisting or future slots.
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
              {availableSlots.map(slot => {
                const { localTimeStr } = getLocalTimeForSlot(dateTime, slot.startTime, timezone);
                return (
                  <button
                    key={slot.id}
                    type="button"
                    onClick={() => {
                      setSelectedSlotId(slot.id);
                      setShowConfirmReschedule(isRescheduling);
                    }}
                    className={`px-3 py-3 border rounded-xl text-center transition-all flex flex-col items-center justify-center cursor-pointer ${
                      selectedSlotId === slot.id
                        ? 'border-blue-600 bg-blue-50/50 text-blue-700 ring-2 ring-blue-500/20'
                        : 'border-slate-200 hover:border-blue-300 hover:bg-slate-50 text-slate-700'
                    }`}
                  >
                    <Clock className={`h-4 w-4 mb-1 ${selectedSlotId === slot.id ? 'text-blue-600' : 'text-slate-400'}`} />
                    <span className="text-xs font-bold font-mono">{localTimeStr}</span>
                    <span className="text-[11px] text-slate-400 font-semibold mt-0.5">Available</span>
                  </button>
                );
              })}
            </div>
          )}

          {/* Confirm Reschedule alert card */}
          {showConfirmReschedule && selectedSlot && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 space-y-3 animate-fade-in text-xs text-blue-900">
              <div className="flex gap-2.5 items-start">
                <AlertCircle className="h-5 w-5 text-blue-600 shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-bold">Confirm Reschedule</h4>
                  <p className="text-blue-700 mt-1">Your current booking will be released and changed to the newly selected slot: <strong>{getLocalTimeForSlot(dateTime, selectedSlot.startTime, timezone).localTimeStr}</strong>.</p>
                </div>
              </div>
            </div>
          )}

          {/* Booking form errors: in-page banner above the form (the
              form itself only renders once a slot is selected, and the
              no-slot validation must stay reachable). */}
          {formError && (
            <div
              role="alert"
              className="bg-rose-50 border border-rose-300 text-rose-800 rounded-xl px-4 py-2.5 text-xs font-semibold mb-4"
            >
              {formError}
            </div>
          )}

          {/* Booking form */}
          {selectedSlot && (
            <form onSubmit={handleBookSlot} className="border-t border-slate-100 pt-6 space-y-4 animate-fade-in text-xs">
              <div className="bg-blue-50/50 border border-blue-100/50 rounded-xl p-4 flex items-center justify-between">
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-blue-500">Selected Time Slot</p>
                  <p className="font-bold text-slate-800 font-mono mt-0.5">
                    {getLocalTimeForSlot(dateTime, selectedSlot.startTime, timezone).localTimeStr}
                  </p>
                </div>
                <span className="bg-blue-600 text-white text-[10px] font-bold uppercase px-2.5 py-1 rounded-lg">
                  Selected
                </span>
              </div>

              {tokenVerificationSuccess && (
                <div className="bg-emerald-50 border border-emerald-150 rounded-xl p-4 flex items-center gap-3 text-emerald-800 text-xs font-semibold animate-fade-in">
                  <Check className="h-5 w-5 text-emerald-500 shrink-0" />
                  <span>{tokenVerificationSuccess}</span>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-[10px] uppercase font-bold tracking-wider text-slate-500">Your Full Name</label>
                  <div className="relative">
                    <User className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                    <input
                      type="text"
                      placeholder="e.g. Jean-Pierre"
                      value={name}
                      onChange={e => setName(e.target.value)}
                      readOnly={false}
                      className="w-full pl-9 pr-4 py-2 border border-slate-200 rounded-xl outline-none focus:border-blue-500 focus:ring-2 focus:ring-brand-primary/10 font-semibold text-slate-800 bg-white"
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className="text-[10px] uppercase font-bold tracking-wider text-slate-500">
                    Your Email Address {token ? '(Locked)' : ''}
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                    <input
                      type="email"
                      required
                      placeholder="e.g. jean.pierre@example.com"
                      value={token ? hashEmail(email) : email}
                      onChange={e => setEmail(e.target.value)}
                      readOnly={!!token}
                      className={`w-full pl-9 pr-4 py-2 border border-slate-200 rounded-xl outline-none font-semibold text-slate-800 ${
                        token ? 'bg-slate-100 cursor-not-allowed opacity-80' : 'bg-white focus:border-blue-500'
                      }`}
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-[10px] uppercase font-bold tracking-wider text-slate-500">
                  iSATS Application ID {token ? '(Locked)' : ''}
                </label>
                <input
                  type="text"
                  required
                  placeholder={token ? "" : "e.g. app_12345678 (helps synchronize with your candidate file)"}
                  value={token ? hashAppId(applicationId) : applicationId}
                  onChange={e => setApplicationId(e.target.value)}
                  readOnly={!!token}
                  className={`w-full px-4 py-2 border border-slate-200 rounded-xl outline-none font-semibold text-slate-800 ${
                    token ? 'bg-slate-100 cursor-not-allowed opacity-80' : 'bg-white focus:border-blue-500'
                  }`}
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full py-3 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-xl text-xs font-bold transition-all shadow-md shadow-blue-500/10 flex items-center justify-center gap-2 cursor-pointer"
              >
                {submitting ? (
                  <>
                    <Loader className="h-4 w-4 animate-spin" />
                    Reserving appointment...
                  </>
                ) : (
                  <>
                    <Check className="h-4 w-4" />
                    {isRescheduling ? 'Confirm Reschedule & Update Booking' : 'Confirm & Complete Booking'}
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}